// The user manual: short, task-oriented topics in French and English. Each topic belongs to a page of the app (for the
// "?" link) and applies to people who hold a permission (null: everyone). The questions agent searches the same topics.
// Keep in step with the app: when a screen changes, change its topic here.

export type Lang = 'fr' | 'en';
export type Page =
  | 'start'
  | 'home'
  | 'profile'
  | 'requests'
  | 'documents'
  | 'units'
  | 'parties'
  | 'quote-parts'
  | 'finances'
  | 'approvals'
  | 'maintenance'
  | 'governance'
  | 'reservations'
  | 'insurance'
  | 'automation'
  | 'onboarding'
  | 'settings';

export interface Topic {
  id: string;
  page: Page;
  /** Permission needed for the topic to apply (null: everyone). */
  requires: string | null;
  title: Record<Lang, string>;
  /** Steps or short paragraphs, in order. */
  body: Record<Lang, string[]>;
}

export const TOPICS: Topic[] = [
  // --- Everyone
  {
    id: 'sign-in',
    page: 'start',
    requires: null,
    title: { fr: 'Se connecter pour la première fois', en: 'Signing in for the first time' },
    body: {
      fr: [
        'Le conseil vous invite à partir de votre fiche : vous recevez un courriel « Syndiqia » avec un lien pour choisir votre mot de passe.',
        'Le lien est valide quelques jours. S’il a expiré, demandez au conseil de renvoyer l’invitation.',
        'Ensuite, connectez-vous sur app.syndiqia.com avec votre courriel et votre mot de passe. « Mot de passe oublié? » sur la page de connexion vous en envoie un nouveau.',
        'La session reste ouverte 12 heures sans activité (7 jours au plus). Cochez « Se souvenir de moi » sur un appareil personnel pour rester connecté jusqu’à 30 jours; ne le cochez pas sur un ordinateur partagé.',
        'Sur téléphone, ajoutez l’application à l’écran d’accueil (menu du navigateur → « Ajouter à l’écran d’accueil ») pour l’ouvrir comme une application.',
      ],
      en: [
        'The board invites you from your record: you receive a "Syndiqia" email with a link to choose your password.',
        'The link is valid for a few days. If it has expired, ask the board to send the invitation again.',
        'Then sign in at app.syndiqia.com with your email and password. "Forgot password?" on the sign-in page sends you a new one.',
        'Your session stays open for 12 hours without activity (7 days at most). Tick "Remember me" on a personal device to stay signed in for up to 30 days; do not tick it on a shared computer.',
        'On a phone, add the app to your home screen (browser menu → "Add to Home Screen") to open it like an app.',
      ],
    },
  },
  {
    id: 'language-syndicates',
    page: 'start',
    requires: null,
    title: { fr: 'Langue et plusieurs syndicats', en: 'Language and several syndicates' },
    body: {
      fr: [
        'Le bouton « English / Français » en haut change la langue de l’application. La langue de vos courriels se règle dans Mon profil.',
        'Si vous êtes membre de plusieurs syndicats ou associations, choisissez celui que vous voulez consulter sur la page d’accueil; un même compte suffit.',
      ],
      en: [
        'The "English / Français" button at the top switches the app language. The language of your emails is set in My profile.',
        'If you belong to several syndicates or associations, pick the one you want on the home page; one account is enough.',
      ],
    },
  },
  {
    id: 'profile',
    page: 'profile',
    requires: null,
    title: { fr: 'Mettre à jour mes coordonnées', en: 'Updating my contact details' },
    body: {
      fr: [
        'Ouvrez Mon profil. Modifiez votre courriel de contact, votre téléphone, votre langue de communication et votre adresse postale, puis Enregistrer.',
        'Le conseil se sert de ces coordonnées pour les avis et les communications. Votre nom, lui, est modifié par le conseil.',
        'Mon profil montre aussi vos unités.',
        'Membres du conseil et gestionnaires : choisissez la fréquence du résumé par courriel (chaque jour, chaque semaine le lundi, ou jamais).',
      ],
      en: [
        'Open My profile. Change your contact email, phone, language and mailing address, then Save.',
        'The board uses these details for notices and communications. Your name is changed by the board.',
        'My profile also shows your units.',
        'Board members and managers: choose how often you get the email summary (daily, weekly on Mondays, or never).',
      ],
    },
  },
  {
    id: 'sign-in-email',
    page: 'profile',
    requires: null,
    title: { fr: 'Changer mon courriel de connexion', en: 'Changing my sign-in email' },
    body: {
      fr: [
        'Dans Mon profil, section « Courriel de connexion », entrez la nouvelle adresse puis Changer.',
        'Un lien de confirmation est envoyé à la nouvelle adresse. Utilisez-la ensuite pour vous connecter.',
        'Ce courriel est celui de votre compte, pour tous vos syndicats. Votre courriel de contact (pour les avis) peut être différent.',
      ],
      en: [
        'In My profile, "Sign-in email" section, enter the new address and click Change.',
        'A confirmation link is sent to the new address. Use it to sign in from then on.',
        'This is your account email, for all your syndicates. Your contact email (for notices) can be different.',
      ],
    },
  },
  {
    id: 'ask-question',
    page: 'home',
    requires: 'agents.ask',
    title: { fr: 'Poser une question sur les règlements', en: 'Asking a question about the rules' },
    body: {
      fr: [
        'Sur l’accueil, écrivez votre question dans « Une question sur les règlements? » (ex. : puis-je installer un climatiseur?).',
        'La réponse est tirée uniquement des documents partagés par le conseil (déclaration, règlements, procès-verbaux), avec les sources (document et page).',
        'Les questions sur l’utilisation de Syndiqia (« comment faire… ») sont aussi répondues à partir de ce guide.',
        'Si les documents ne répondent pas, « Transmettre ma question au conseil » crée une demande que vous suivez dans Demandes. La réponse automatique ne remplace pas une décision du conseil.',
      ],
      en: [
        'On the home page, type your question in "A question about the rules?" (e.g. may I install an air conditioner?).',
        'The answer comes only from the documents the board shares (declaration, by-laws, minutes), with sources (document and page).',
        'Questions about using Syndiqia ("how do I…") are also answered from this guide.',
        'If the documents do not answer it, "Forward my question to the board" creates a request you follow in Requests. The automatic answer is not a decision of the board.',
      ],
    },
  },
  {
    id: 'make-request',
    page: 'requests',
    requires: 'requests.create',
    title: { fr: 'Signaler un problème ou faire une demande', en: 'Reporting a problem or making a request' },
    body: {
      fr: [
        'Ouvrez Demandes → Nouvelle demande. Choisissez le type (bris ou entretien, question, plainte…), l’unité, et décrivez la situation.',
        'Le conseil reçoit la demande; vous suivez les réponses dans le fil de la demande et par courriel.',
        'Photos et documents : joignez jusqu’à 5 fichiers (photo, PDF; 10 Mo chacun) à votre message. Les pièces jointes d’un courriel envoyé au syndicat sont ajoutées aussi.',
        'Vous pouvez aussi écrire à l’adresse du syndicat indiquée en haut de la page Demandes : votre courriel devient une demande. Pour répondre au conseil, répondez simplement à son courriel; votre réponse s’ajoute à la demande.',
        'Urgence (fuite d’eau, incendie, panne de chauffage en hiver) : appelez d’abord le numéro d’urgence de votre immeuble, puis faites la demande.',
      ],
      en: [
        'Open Requests → New request. Choose the type (repair, question, complaint…), the unit, and describe the situation.',
        'The board receives it; you follow the replies in the request thread and by email.',
        'Photos and documents: attach up to 5 files (photo, PDF; 10 MB each) to your message. Attachments of an email sent to the syndicate are added too.',
        'You can also write to the syndicate’s address shown at the top of the Requests page: your email becomes a request. To answer the board, just reply to its email; your reply is added to the request.',
        'Emergency (water leak, fire, no heat in winter): call your building’s emergency number first, then make the request.',
      ],
    },
  },
  {
    id: 'documents-read',
    page: 'documents',
    requires: 'documents.read',
    title: { fr: 'Trouver un document', en: 'Finding a document' },
    body: {
      fr: [
        'Documents regroupe la déclaration de copropriété, les règlements, les procès-verbaux approuvés, les états financiers et les avis.',
        'La recherche trouve un mot dans le texte des documents et indique la page. Ouvrir affiche le fichier.',
      ],
      en: [
        'Documents holds the declaration of co-ownership, by-laws, approved minutes, financial statements and notices.',
        'Search finds a word in the text of the documents and shows the page. Open displays the file.',
      ],
    },
  },
  {
    id: 'my-account',
    page: 'units',
    requires: null,
    title: { fr: 'Voir le compte de mon unité', en: 'Seeing my unit’s account' },
    body: {
      fr: [
        'Unités → votre unité : quote-part, propriétaires, et le compte (contributions facturées, paiements reçus, solde).',
        'Vous ne voyez que le compte de vos propres unités. Un occupant (locataire) ne voit pas le compte du propriétaire.',
      ],
      en: [
        'Units → your unit: share, owners, and the account (contributions billed, payments received, balance).',
        'You only see the account of your own units. An occupant (tenant) does not see the owner’s account.',
      ],
    },
  },
  {
    id: 'insurance-submit',
    page: 'profile',
    requires: 'insurance.submit',
    title: { fr: 'Envoyer mon attestation d’assurance', en: 'Sending my insurance certificate' },
    body: {
      fr: [
        'Mon profil → Mon assurance → Téléverser l’attestation (PDF ou photo).',
        'Syndiqia lit l’assureur, le numéro de police, le montant de responsabilité civile et les dates; vérifiez-les et corrigez au besoin.',
        'Le conseil accepte ou refuse l’attestation (avec un motif). Vous recevez un rappel 30 jours avant l’échéance.',
        'Seul le conseil voit votre attestation (renseignements personnels).',
      ],
      en: [
        'My profile → My insurance → Upload the certificate (PDF or photo).',
        'Syndiqia reads the insurer, policy number, liability amount and dates; check them and correct if needed.',
        'The board accepts or rejects the certificate (with a reason). You get a reminder 30 days before it expires.',
        'Only the board sees your certificate (personal information).',
      ],
    },
  },
  {
    id: 'book',
    page: 'reservations',
    requires: 'reservations.book',
    title: { fr: 'Réserver une installation', en: 'Booking a facility' },
    body: {
      fr: [
        'Réservations → choisissez l’installation (salle commune, terrain, ascenseur de déménagement…), la date et l’heure.',
        'Les règles s’appliquent automatiquement : délai maximal, nombre de réservations par unité, frais ou dépôt, périodes fermées. Deux réservations ne peuvent pas se chevaucher.',
        'Certaines installations demandent l’approbation du conseil : la réservation reste « en attente » jusqu’à sa décision. Vous recevez un courriel de confirmation.',
        'Annulez depuis la liste de vos réservations.',
      ],
      en: [
        'Reservations → choose the facility (party room, court, move elevator…), the date and time.',
        'Rules apply automatically: how far ahead, bookings per unit, fees or deposit, closed periods. Two bookings can never overlap.',
        'Some facilities need the board’s approval: the booking stays "pending" until it decides. You receive a confirmation email.',
        'Cancel from the list of your bookings.',
      ],
    },
  },
  {
    id: 'assemblies-member',
    page: 'governance',
    requires: 'governance.view',
    title: { fr: 'Consulter les assemblées', en: 'Seeing the assemblies' },
    body: {
      fr: [
        'Assemblées montre les assemblées auxquelles vous êtes convoqué, avec l’ordre du jour et les résolutions proposées, et la composition du conseil.',
        'L’avis de convocation officiel vous est aussi envoyé par courriel. Les procès-verbaux approuvés sont dans Documents.',
      ],
      en: [
        'Assemblies shows the meetings you are convened to, with the agenda and proposed resolutions, and the board’s composition.',
        'The official notice is also emailed to you. Approved minutes are in Documents.',
      ],
    },
  },

  // --- Board
  {
    id: 'board-home',
    page: 'home',
    requires: 'ledger.read',
    title: { fr: 'L’accueil du conseil : la liste « À faire »', en: 'The board’s home: the "To do" list' },
    body: {
      fr: [
        'L’accueil rassemble ce qui attend le conseil : approbations, transactions bancaires à concilier, appels de contributions à émettre, virements entre fonds, échéances d’entretien et de conformité, attestations d’assurance à vérifier.',
        'Chaque ligne mène à l’écran où on la traite. Quand tout est fait, la liste l’indique.',
      ],
      en: [
        'The home page gathers what is waiting for the board: approvals, bank transactions to reconcile, contribution calls to issue, transfers between funds, maintenance and compliance deadlines, insurance certificates to check.',
        'Each line leads to the screen where it is handled. When everything is done, the list says so.',
      ],
    },
  },
  {
    id: 'approvals',
    page: 'approvals',
    requires: 'collections.approve',
    title: { fr: 'Approuver ce que les agents préparent', en: 'Approving what the agents prepare' },
    body: {
      fr: [
        'Les agents préparent; le conseil décide. Tout ce qui engage le syndicat (rappels de paiement, avis à tous, mouvements d’argent) attend dans Approbations.',
        'Ouvrez l’élément, vérifiez l’aperçu, retirez au besoin certaines personnes (Inclure), puis Approuver ou Refuser avec un motif.',
        'Rappels de paiement : si des dépôts bancaires ne sont pas conciliés, un avertissement s’affiche — conciliez d’abord, certains ont peut-être déjà payé. Ceux qui paient entre-temps sont retirés automatiquement.',
      ],
      en: [
        'Agents prepare; the board decides. Anything that commits the syndicate (payment reminders, notices to everyone, money movements) waits in Approvals.',
        'Open the item, check the preview, untick people if needed (Include), then Approve or Reject with a reason.',
        'Payment reminders: if bank deposits are not reconciled, a warning appears — reconcile first, some may already have paid. People who pay in the meantime are removed automatically.',
      ],
    },
  },
  {
    id: 'requests-board',
    page: 'requests',
    requires: 'requests.manage',
    title: { fr: 'Traiter les demandes des copropriétaires', en: 'Handling owners’ requests' },
    body: {
      fr: [
        'Demandes → Ouvertes. Chaque nouvelle demande arrive avec une suggestion de Syndiqia : une réponse tirée des documents, ou un bon de travail proposé pour un bris.',
        'Vérifiez et modifiez la suggestion avant de l’envoyer; rien n’est envoyé sans vous, sauf si le conseil a mis les réponses en mode automatique (voir Automatisation). Une note interne n’est jamais montrée ni envoyée au demandeur.',
        'Fermez la demande quand elle est réglée. Les réponses reçues par courriel s’ajoutent au fil, avec leurs pièces jointes; vous pouvez aussi joindre des fichiers à vos réponses (les fichiers d’une note interne restent au conseil).',
        'Les courriels envoyés à l’adresse du syndicat (en haut de la page Demandes) deviennent des demandes. Un courriel d’une adresse absente du registre arrive avec une note interne : associez la personne à une fiche pour lui répondre. Les réponses automatiques (absence du bureau) et les pourriels sont ignorés.',
      ],
      en: [
        'Requests → Open. Each new request comes with a Syndiqia suggestion: an answer from the documents, or a proposed work order for a repair.',
        'Check and edit the suggestion before sending; nothing is sent without you, unless the board set replies to automatic (see Automation). An internal note is never shown or emailed to the requester.',
        'Close the request when it is settled. Replies received by email are added to the thread, with their attachments; you can also attach files to your replies (files on an internal note stay with the board).',
        'Emails sent to the syndicate’s address (top of the Requests page) become requests. An email from an address not in the registry arrives with an internal note: link the person to a record to answer them. Automatic replies (out of office) and spam are ignored.',
      ],
    },
  },
  {
    id: 'documents-board',
    page: 'documents',
    requires: 'documents.manage',
    title: { fr: 'Ajouter et partager des documents', en: 'Adding and sharing documents' },
    body: {
      fr: [
        'Documents → Ajouter : choisissez le fichier (PDF), la catégorie, la date et la visibilité : « Tous les copropriétaires et occupants » ou « Le conseil seulement ».',
        'Le texte est indexé pour la recherche et pour les réponses automatiques. Les réponses aux copropriétaires citent seulement les documents qui leur sont visibles.',
      ],
      en: [
        'Documents → Add: choose the file (PDF), category, date and visibility: "All co-owners and occupants" or "The board only".',
        'The text is indexed for search and automatic answers. Answers to co-owners cite only the documents visible to them.',
      ],
    },
  },
  {
    id: 'members-invite',
    page: 'parties',
    requires: 'registry.contacts.read',
    title: { fr: 'Fiches des copropriétaires et invitations', en: 'Co-owner records and invitations' },
    body: {
      fr: [
        'Copropriétaires → une personne : coordonnées, unités possédées (avec l’historique), occupation, accès et rôles.',
        'Pour donner accès en ligne : la fiche doit avoir un courriel, puis Inviter. La personne reçoit un courriel pour choisir son mot de passe; « Renvoyer l’invitation » si besoin.',
        'Les occupants (locataires) s’ajoutent depuis la page de l’unité, avec la date d’arrivée; ils peuvent faire des demandes et réserver, sans voir le compte du propriétaire.',
      ],
      en: [
        'Co-owners → a person: contact details, units owned (with history), occupancy, access and roles.',
        'To give online access: the record needs an email, then Invite. The person receives an email to choose a password; "Resend the invitation" if needed.',
        'Occupants (tenants) are added from the unit’s page, with their move-in date; they can make requests and book, without seeing the owner’s account.',
      ],
    },
  },
  {
    id: 'board-roles',
    page: 'parties',
    requires: 'members.roles',
    title: { fr: 'Nommer un administrateur, un gestionnaire, et les fonctions du conseil', en: 'Appointing a director, a manager, and board positions' },
    body: {
      fr: [
        'Sur la fiche d’une personne qui a un accès : « Nommer administrateur » ou « Nommer gestionnaire » (ou retirer le rôle). Ajoutez une note, par exemple « élue à l’AGA du 25 mars ». Seul un administrateur peut le faire; le dernier administrateur ne peut pas être retiré.',
        'Pour un administrateur, la section « Fonctions au conseil » inscrit sa fonction (président, vice-président, secrétaire, trésorier, administrateur) et la durée du mandat. « Terminer aujourd’hui » clôt un mandat.',
        'La composition du conseil s’affiche dans Assemblées; les mandats qui finissent dans les 90 jours sont signalés pour la prochaine assemblée.',
        'L’historique garde qui a eu quel rôle, et quand.',
      ],
      en: [
        'On the record of someone who has access: "Make director" or "Make manager" (or remove the role). Add a note, e.g. "elected at the March 25 AGM". Only a director can do this; the last director cannot be removed.',
        'For a director, the "Board positions" section records the position (president, vice-president, secretary, treasurer, director) and the term. "End today" closes a term.',
        'The board’s composition shows in Assemblies; terms ending within 90 days are flagged for the next meeting.',
        'The history keeps who held which role, and when.',
      ],
    },
  },
  {
    id: 'access-review',
    page: 'settings',
    requires: 'registry.configure',
    title: { fr: 'Revoir les accès (Loi 25)', en: 'Reviewing access (Law 25)' },
    body: {
      fr: [
        'Paramètres → Revue des accès : qui a accès, avec quel rôle, invitations en attente, comptes jamais utilisés, liens de fournisseurs actifs.',
        'Retirez les accès qui ne servent plus (ancien propriétaire, locataire parti). Les rôles du conseil se changent sur la fiche de la personne.',
      ],
      en: [
        'Settings → Access review: who has access, with which role, pending invitations, accounts never used, active vendor links.',
        'Remove access that is no longer needed (former owner, tenant who left). Board roles are changed on the person’s record.',
      ],
    },
  },
  {
    id: 'support-access',
    page: 'settings',
    requires: 'members.roles',
    title: { fr: 'Accès du soutien Syndiqia à vos données', en: 'Syndiqia support access to your data' },
    body: {
      fr: [
        'L’équipe Syndiqia n’a aucun accès aux données de votre syndicat par défaut. Si vous demandez de l’aide, une personne du soutien peut demander un accès temporaire; vous recevez un courriel.',
        'Paramètres → Accès du soutien Syndiqia : le motif, la durée demandée et la personne. Approuver ouvre un accès en lecture seule jusqu’à l’échéance; Refuser le ferme.',
        'Pendant l’accès, le soutien voit ce que voit le conseil, sans rien pouvoir modifier, et jamais les attestations d’assurance des copropriétaires. Chaque page consultée est consignée : le nombre et la dernière consultation s’affichent.',
        '« Retirer l’accès » y met fin immédiatement. Seuls les administrateurs décident.',
      ],
      en: [
        'The Syndiqia team has no access to your syndicate’s data by default. If you ask for help, a support person may request temporary access; you receive an email.',
        'Settings → Syndiqia support access: the reason, the requested duration and the person. Approve opens read-only access until it expires; Refuse closes it.',
        'During the access, support sees what the board sees, without changing anything, and never owners’ insurance certificates. Every page viewed is logged: the count and the last view are shown.',
        '"Withdraw access" ends it immediately. Only directors decide.',
      ],
    },
  },
  {
    id: 'quote-parts',
    page: 'quote-parts',
    requires: 'registry.configure',
    title: { fr: 'Quotes-parts', en: 'Shares (relative values)' },
    body: {
      fr: [
        'Les quotes-parts (valeurs relatives de la déclaration) répartissent les contributions et pondèrent les votes en assemblée.',
        'Une nouvelle répartition s’ajoute avec sa date d’entrée en vigueur; l’ancienne est conservée dans l’historique. Le total doit égaler 100 %.',
      ],
      en: [
        'Shares (relative values from the declaration) allocate contributions and weight votes at meetings.',
        'A new allocation is added with its effective date; the previous one is kept in the history. The total must equal 100%.',
      ],
    },
  },
  {
    id: 'budget-calls',
    page: 'finances',
    requires: 'ledger.read',
    title: { fr: 'Budget et appels de contributions', en: 'Budget and contribution calls' },
    body: {
      fr: [
        'Finances → créez l’exercice financier (une fois par année), puis Budget : contributions, autres revenus et dépenses, par fonds (fonds d’exploitation, fonds de prévoyance). Adoptez-le une fois voté.',
        'Le budget adopté produit les appels de contributions mensuels, répartis selon les quotes-parts. L’accueil indique quand un appel est à émettre.',
        'Le fonds d’exploitation et le fonds de prévoyance sont toujours séparés. Si tout est payé dans un seul compte bancaire, l’accueil rappelle le virement à faire vers les autres fonds.',
      ],
      en: [
        'Finances → create the fiscal year (once a year), then Budget: contributions, other revenue and expenses, per fund (operating, contingency). Adopt it once voted.',
        'The adopted budget produces the monthly contribution calls, allocated by shares. The home page says when a call is due.',
        'The operating and contingency funds are always kept apart. If everything is paid into one bank account, the home page reminds you of the transfer to make to the other funds.',
      ],
    },
  },
  {
    id: 'bank-reconcile',
    page: 'finances',
    requires: 'ledger.read',
    title: { fr: 'Importer le relevé bancaire et concilier', en: 'Importing the bank statement and reconciling' },
    body: {
      fr: [
        'Finances → Banque → Importer un relevé bancaire : le fichier CSV exporté du site de votre banque. Indiquez les colonnes la première fois; Syndiqia s’en souvient. Une transaction déjà importée n’est jamais ajoutée deux fois.',
        'Syndiqia propose une correspondance pour chaque dépôt (paiement de tel copropriétaire) et chaque retrait (dépense). Rien n’est inscrit tant que le conseil ne confirme pas.',
        'Le journal général garde chaque écriture; une erreur se corrige par une écriture de contrepassation, jamais en effaçant.',
      ],
      en: [
        'Finances → Bank → Import a bank statement: the CSV file exported from your bank’s website. Map the columns the first time; Syndiqia remembers. A transaction already imported is never added twice.',
        'Syndiqia suggests a match for each deposit (payment from an owner) and each withdrawal (expense). Nothing is posted until the board confirms.',
        'The general journal keeps every entry; a mistake is corrected with a reversing entry, never by deleting.',
      ],
    },
  },
  {
    id: 'collections',
    page: 'finances',
    requires: 'ledger.read',
    title: { fr: 'Rappels de paiement et comptes en souffrance', en: 'Payment reminders and arrears' },
    body: {
      fr: [
        'Syndiqia prépare les rappels à 15 et 30 jours de retard; ils attendent l’approbation d’un administrateur avant l’envoi. Ils ne sont pas préparés tant que des dépôts bancaires restent à concilier.',
        'Après deux rappels sans effet, le dossier est transmis au conseil, qui décide de la suite (entente de paiement, mise en demeure…). Aucune poursuite n’est faite par Syndiqia.',
        'L’accueil montre le total en retard et les unités concernées.',
      ],
      en: [
        'Syndiqia prepares reminders at 15 and 30 days late; they wait for a director’s approval before sending. They are not prepared while bank deposits remain unreconciled.',
        'After two reminders without effect, the file goes to the board, which decides what comes next (payment plan, formal notice…). Syndiqia never takes legal action.',
        'The home page shows the total overdue and the units concerned.',
      ],
    },
  },
  {
    id: 'work-orders',
    page: 'maintenance',
    requires: 'maintenance.read',
    title: { fr: 'Bons de travail et fournisseurs', en: 'Work orders and vendors' },
    body: {
      fr: [
        'Entretien → Nouveau bon de travail, ou à partir d’une demande (Syndiqia en propose un pour un bris). Indiquez la composante, la priorité, le fournisseur et la date prévue.',
        'Fournisseurs : vérifiez la licence RBQ et l’attestation d’assurance avant d’attribuer des travaux.',
        'Terminé : le coût réel et l’intervention s’ajoutent au carnet d’entretien.',
      ],
      en: [
        'Maintenance → New work order, or from a request (Syndiqia proposes one for a repair). Set the component, priority, vendor and scheduled date.',
        'Vendors: check the RBQ licence and insurance certificate before assigning work.',
        'Completed: the actual cost and the work are added to the maintenance logbook.',
      ],
    },
  },
  {
    id: 'vendor-link',
    page: 'maintenance',
    requires: 'maintenance.vendor',
    title: { fr: 'Donner accès à un entrepreneur', en: 'Giving a contractor access' },
    body: {
      fr: [
        'Sur un bon de travail, choisissez le fournisseur puis « Envoyer le lien par courriel » (ou « Créer un lien à copier ») : un lien sécurisé, sans compte à créer.',
        'Par ce lien, il voit seulement ce bon de travail : il peut l’accepter, proposer une date, joindre une soumission, une facture ou des photos, et le marquer terminé pour approbation.',
        'Une fois le travail terminé, le lien sert encore 60 jours, seulement pour transmettre la facture. Il prend fin aussi à son expiration ou avec « Révoquer le lien ». Le bon de travail indique quand le lien a été ouvert.',
      ],
      en: [
        'On a work order, choose the vendor, then "Email the link" (or create a link to copy): a secure link, with no account to create.',
        'Through it they see only this work order: accept it, propose a date, attach a quote, invoice or photos, and mark it done for approval.',
        'Once the work is completed, the link still works for 60 days, only to send the invoice. It also ends when it expires or when you revoke it. The work order shows when the link was last opened.',
      ],
    },
  },
  {
    id: 'quotes',
    page: 'maintenance',
    requires: 'maintenance.manage',
    title: { fr: 'Demander et comparer des soumissions', en: 'Requesting and comparing quotes' },
    body: {
      fr: [
        'Sur un bon de travail sans fournisseur : Soumissions → « Demander des soumissions ». Cochez jusqu’à 5 fournisseurs (ils doivent avoir un courriel) et, au besoin, la date de réponse souhaitée.',
        'Chacun reçoit son propre lien, sans compte. Il voit seulement la description du travail (ni les autres fournisseurs, ni l’historique) et répond avec son prix avant taxes, son délai, la validité et sa soumission en PDF, ou décline.',
        'L’agent lit chaque soumission (prix, validité, exclusions). Le tableau compare les réponses, la plus basse en évidence; les exclusions lues sont affichées, mais lisez toujours le document.',
        '« Retenir » confie le bon de travail à ce fournisseur, avec son prix comme estimation; il reçoit le lien du bon de travail, les autres sont informés, et les liens de soumission se ferment.',
      ],
      en: [
        'On a work order without a contractor: Quotes → "Request quotes". Tick up to 5 contractors (they need an email) and, if you wish, the date you want answers by.',
        'Each one gets their own link, no account needed. They see only the job description (not the other contractors, not the history) and answer with their price before taxes, timing, validity and their quote as a PDF, or decline.',
        'The agent reads each quote (price, validity, exclusions). The table compares the answers, lowest highlighted; exclusions read are shown, but always read the document.',
        '"Retain" assigns the work order to that contractor, with their price as the estimate; they receive the work order link, the others are told, and the quote links close.',
      ],
    },
  },
  {
    id: 'invoices',
    page: 'maintenance',
    requires: 'maintenance.manage',
    title: { fr: 'Factures des fournisseurs', en: 'Contractor invoices' },
    body: {
      fr: [
        'Les fournisseurs envoient leurs factures par courriel à l’adresse de facturation du syndicat, factures@… (indiquée sur la page d’un bon de travail et dans le courriel envoyé au fournisseur). Donnez-la aux fournisseurs une fois : elle peut devenir l’adresse de facturation dans leur logiciel.',
        'Chaque facture reçue est lue, puis associée au fournisseur (adresse de l’expéditeur) et au bon de travail : le numéro du bon de travail indiqué sur la facture, ou le seul travail en cours de ce fournisseur. Sinon, elle attend dans « Factures à classer » (page Entretien), avec des suggestions : choisissez le bon de travail et « Classer », ou « Pas une facture ».',
        'Un courriel sans PDF (logiciel qui envoie seulement un lien) est conservé tel quel, avec le montant lu dans le courriel s’il y figure. Une facture reçue par la poste s’ajoute sur le bon de travail (Factures → « Ajouter une facture reçue »); le fournisseur peut aussi la téléverser par son lien.',
        'L’agent lit le PDF (numéro, date, sous-total, TPS, TVQ, total). Pour une photo ou un PDF numérisé, saisissez les montants. « Corriger les montants » permet d’ajuster ce qui a été lu.',
        'Syndiqia vérifie la facture contre le bon de travail : bon fournisseur, travail terminé, montant dans l’estimation (avant taxes, tolérance de 10 %), numéro non répété, TPS (5 %) et TVQ (9,975 %) exactes, total juste. Chaque écart est signalé; « Conforme au bon de travail » sinon.',
        '« Approuver » (fonds et compte de dépense au choix, par défaut entretien et réparations du fonds d’administration) inscrit la dépense et le montant dû au fournisseur (compte 2010); le coût réel du bon de travail est mis à jour. Le paiement se fait à la banque, puis se concilie comme les autres transactions.',
        '« Refuser » demande un motif, visible par le fournisseur sur son lien.',
        'Mode automatique (Automatisation → Approbation des factures) : une facture conforme, lue avec confiance et sous le montant que vous fixez, est approuvée seule et consignée. Aucun paiement n’est jamais fait automatiquement.',
      ],
      en: [
        'Contractors email their invoices to the syndicate’s billing address, factures@… (shown on a work order page and in the email sent to the contractor). Give it to contractors once: it can become the billing address in their software.',
        'Each invoice received is read, then tied to the contractor (sender address) and the work order: the work order number written on the invoice, or that contractor’s only current job. Otherwise it waits in "Invoices to sort" (Maintenance page) with suggestions: choose the work order and "Sort", or "Not an invoice".',
        'An email with no PDF (software that only sends a link) is kept as is, with the amount read from the email when it is there. A paper invoice is added on the work order (Invoices → "Add an invoice received"); the contractor can also upload it through their link.',
        'The agent reads the PDF (number, date, subtotal, GST, QST, total). For a photo or a scanned PDF, enter the amounts. "Correct the amounts" adjusts what was read.',
        'Syndiqia checks the invoice against the work order: right contractor, work completed, amount within the estimate (before taxes, 10% tolerance), number not repeated, correct GST (5%) and QST (9.975%), right total. Each discrepancy is flagged; otherwise "Matches the work order".',
        '"Approve" (fund and expense account of your choice, by default building maintenance in the operating fund) records the expense and the amount owed to the contractor (account 2010); the work order\'s actual cost is updated. Payment is made at the bank, then reconciled like other transactions.',
        '"Reject" asks for a reason, visible to the contractor on their link.',
        'Automatic mode (Automation → Supplier invoice approvals): a compliant invoice, read with confidence and under the amount you set, is approved on its own and logged. No payment is ever made automatically.',
      ],
    },
  },
  {
    id: 'logbook-calendar',
    page: 'maintenance',
    requires: 'maintenance.read',
    title: { fr: 'Carnet d’entretien, composantes et calendrier', en: 'Logbook, components and calendar' },
    body: {
      fr: [
        'Composantes : les parties communes avec leur âge et leur durée de vie (base du carnet et de l’étude du fonds de prévoyance).',
        'Carnet d’entretien : chaque inspection, réparation ou remplacement, dans l’ordre. Une entrée ne se modifie pas; une correction s’ajoute. Le carnet et l’étude sont produits par des professionnels (ingénieur, architecte, technologue); Syndiqia les tient à jour et les alimente.',
        'Calendrier : obligations légales (Loi 16), assurances et entretien préventif, avec échéances. Le conseil reçoit un rappel 60, 30 et 7 jours avant, puis en cas de retard.',
      ],
      en: [
        'Components: the common parts with their age and service life (the basis of the logbook and of the contingency fund study).',
        'Maintenance logbook: each inspection, repair or replacement, in order. An entry is never changed; a correction is added. The logbook and the study are produced by professionals (engineer, architect, technologist); Syndiqia keeps them current and feeds them.',
        'Calendar: legal obligations (Bill 16), insurance and preventive maintenance, with due dates. The board is reminded 60, 30 and 7 days before, then when overdue.',
      ],
    },
  },
  {
    id: 'meeting-prepare',
    page: 'governance',
    requires: 'governance.manage',
    title: { fr: 'Préparer une assemblée ou une réunion du conseil', en: 'Preparing an assembly or a board meeting' },
    body: {
      fr: [
        'Assemblées → Nouvelle réunion : le type (assemblée annuelle, extraordinaire, réunion du conseil), la date, l’heure et le lieu. L’ordre du jour standard est ajouté.',
        'Réunions régulières du conseil : choisissez « Répéter » (chaque mois, aux deux mois…) et le nombre de réunions; le calendrier de l’année est créé d’un coup.',
        '« Préparer avec l’agent » : l’agent suggère des points tirés des suivis, des demandes, de l’entretien et des échéances. Vous gardez ceux qui conviennent et rédigez les résolutions.',
        'Avis de convocation : vérifiez-le puis envoyez-le; l’envoi passe par l’approbation et chaque courriel garde sa preuve de transmission. Les délais d’avis viennent des règles des assemblées (Paramètres).',
      ],
      en: [
        'Assemblies → New meeting: the type (annual, special, board meeting), date, time and place. The standard agenda is added.',
        'Regular board meetings: choose "Repeat" (every month, every two months…) and the number of meetings; the year’s calendar is created at once.',
        '"Prepare with the agent": the agent proposes items from follow-ups, requests, maintenance and deadlines. Keep the right ones and write the resolutions.',
        'Notice: check it, then send it; sending goes through approval and every email keeps its proof of delivery. Notice periods come from the meeting rules (Settings).',
      ],
    },
  },
  {
    id: 'meeting-run',
    page: 'governance',
    requires: 'governance.manage',
    title: { fr: 'Pendant la réunion : présences, quorum, votes, notes', en: 'During the meeting: attendance, quorum, votes, notes' },
    body: {
      fr: [
        'Feuille de présence : cochez les présents et les procurations; le quorum se calcule selon les quotes-parts (réunion du conseil : selon le nombre d’administrateurs). Au conseil, le président préside par défaut; « Présider » change la personne.',
        'Votes : pour chaque résolution, inscrivez les voix pour, contre et abstentions; la majorité requise (simple, absolue, double…) est appliquée. Au conseil, si vos règlements donnent une voix prépondérante au président, elle tranche une égalité.',
        'Notes : prenez des notes par point, ou enregistrez la réunion; Syndiqia transcrit l’enregistrement.',
        'Suivis : ajoutez les actions à faire avec un responsable et une échéance; elles restent visibles jusqu’à ce qu’elles soient faites.',
      ],
      en: [
        'Attendance: tick who is present and the proxies; quorum is computed from the shares (board meeting: from the number of directors). At the board, the president chairs by default; "Make chair" changes who does.',
        'Votes: for each resolution, record the votes for, against and abstaining; the required majority (simple, absolute, double…) is applied. At the board, if your by-laws give the chair a casting vote, it breaks a tie.',
        'Notes: take notes per item, or record the meeting; Syndiqia transcribes the recording.',
        'Follow-ups: add actions with a person responsible and a due date; they stay visible until done.',
      ],
    },
  },
  {
    id: 'minutes',
    page: 'governance',
    requires: 'governance.manage',
    title: { fr: 'Procès-verbal', en: 'Minutes' },
    body: {
      fr: [
        'Après la réunion, « Rédiger le procès-verbal avec l’agent » : l’agent prépare un brouillon à partir de l’ordre du jour, des présences, des votes, des notes et de la transcription.',
        'Relisez et corrigez-le, puis approuvez-le. Le procès-verbal approuvé est publié dans Documents (visible aux copropriétaires pour une assemblée, au conseil pour une réunion du conseil).',
      ],
      en: [
        'After the meeting, "Draft the minutes with the agent": the agent prepares a draft from the agenda, attendance, votes, notes and transcript.',
        'Review and correct it, then approve it. Approved minutes are published in Documents (visible to co-owners for an assembly, to the board for a board meeting).',
      ],
    },
  },
  {
    id: 'written-resolutions',
    page: 'governance',
    requires: 'governance.read',
    title: { fr: 'Résolution écrite du conseil (sans réunion)', en: 'Board written resolution (without a meeting)' },
    body: {
      fr: [
        'Assemblées → Résolutions écrites → Nouvelle résolution écrite : un titre et le texte (« Il est résolu que… »).',
        '« Envoyer aux administrateurs pour signature » : chaque administrateur en fonction reçoit un courriel; le texte ne peut plus être modifié.',
        'Chaque administrateur ouvre la résolution et clique « Je signe cette résolution ». L’accueil lui rappelle qu’une signature est attendue.',
        'Quand tous ont signé, la résolution est adoptée à la date de la dernière signature et s’ajoute au registre des résolutions. Une résolution écrite exige la signature de tous les administrateurs.',
      ],
      en: [
        'Assemblies → Written resolutions → New written resolution: a title and the text ("Il est résolu que…").',
        '"Send to the directors for signature": each director in office receives an email; the text can no longer be changed.',
        'Each director opens the resolution and clicks "I sign this resolution". The home page reminds them a signature is expected.',
        'When all have signed, the resolution is adopted on the date of the last signature and added to the register of resolutions. A written resolution needs every director’s signature.',
      ],
    },
  },
  {
    id: 'registers',
    page: 'governance',
    requires: 'governance.read',
    title: { fr: 'Registre des résolutions et livre des procès-verbaux', en: 'Register of resolutions and minute book' },
    body: {
      fr: [
        'Assemblées → Registres : le registre des résolutions (conseil d’administration ou assemblées), numérotées par année, avec la décision de chacune, et le livre des procès-verbaux approuvés.',
        'Ils se tiennent à jour seuls à partir des votes fermés, des procès-verbaux approuvés et des résolutions écrites adoptées.',
        '« Imprimer » produit une version papier (ou PDF) des registres.',
      ],
      en: [
        'Assemblies → Registers: the register of resolutions (board or assemblies), numbered by year, with each decision, and the book of approved minutes.',
        'They keep themselves up to date from closed votes, approved minutes and adopted written resolutions.',
        '"Print" produces a paper (or PDF) copy of the registers.',
      ],
    },
  },
  {
    id: 'reservations-manage',
    page: 'reservations',
    requires: 'reservations.manage',
    title: { fr: 'Configurer les installations réservables', en: 'Setting up bookable facilities' },
    body: {
      fr: [
        'Réservations → Installations : ajoutez une ressource (salle, terrain, ascenseur…) et ses règles : durée, délai de réservation, quota par unité, frais, dépôt, approbation requise, périodes fermées.',
        'Les frais de réservation sont inscrits au compte de l’unité. Les réservations en attente d’approbation apparaissent dans la liste du conseil.',
      ],
      en: [
        'Reservations → Facilities: add a resource (room, court, elevator…) and its rules: length, booking window, quota per unit, fee, deposit, approval needed, closed periods.',
        'Booking fees are posted to the unit’s account. Bookings waiting for approval appear in the board’s list.',
      ],
    },
  },
  {
    id: 'insurance-board',
    page: 'insurance',
    requires: 'insurance.manage',
    title: { fr: 'Suivre les assurances des copropriétaires', en: 'Tracking co-owners’ insurance' },
    body: {
      fr: [
        'Assurances : l’état de chaque unité (valide, expire bientôt, expirée, manquante, insuffisante, à vérifier) et le montant minimal exigé (à régler selon la déclaration).',
        'Vérifiez les attestations reçues : comparez les données lues avec le fichier, puis Accepter ou Refuser avec un motif. Le conseil peut aussi téléverser une attestation reçue sur papier.',
        '« Demander les attestations manquantes » envoie un courriel à ceux qui n’en ont pas fourni. Les rappels avant l’échéance partent automatiquement.',
      ],
      en: [
        'Insurance: each unit’s status (valid, expiring, expired, missing, insufficient, to review) and the required minimum (set according to the declaration).',
        'Review received certificates: compare what was read with the file, then Accept or Reject with a reason. The board can also upload a certificate received on paper.',
        '"Request missing certificates" emails those who have not provided one. Reminders before expiry go out automatically.',
      ],
    },
  },
  {
    id: 'automation',
    page: 'automation',
    requires: 'automation.view',
    title: { fr: 'Choisir ce que les agents font seuls', en: 'Choosing what the agents do on their own' },
    body: {
      fr: [
        'Automatisation montre chaque processus automatisé (réponses aux demandes, rappels de paiement, conciliation bancaire, attestations d’assurance, envoi des travaux aux fournisseurs, factures des fournisseurs, réservations) et son mode.',
        'Confirmation (réglage initial) : l’agent prépare, une personne du conseil vérifie et confirme. Automatique : l’agent agit seul, dans des garde-fous précis, et le consigne.',
        'Commencez en confirmation; quand les propositions d’un processus sont justes depuis un moment, un administrateur le passe en automatique (avec une note pour l’historique). On peut revenir en confirmation à tout moment.',
        'Pour savoir quand passer en automatique, regardez la fiabilité de chaque processus : sur 90 jours, combien de propositions de l’agent le conseil a confirmées telles quelles. « Prêt pour le mode automatique » s’affiche après au moins 10 décisions, dont 95 % confirmées.',
        'Envoi des travaux aux fournisseurs : fixez d’abord le montant autorisé sans soumission (par exemple 500 $). En automatique, le fournisseur reçoit le bon de travail avec ce montant; au-delà, il doit transmettre une soumission. Les urgences restent toujours humaines.',
        'Approbation des factures : fixez le montant maximal d’une facture approuvée seule. Seules les factures conformes au bon de travail et lues avec confiance sont approuvées; aucun paiement n’est fait.',
        'Approbation des réservations : une réservation conforme aux règles est approuvée seule si l’unité n’a aucun retard de plus de 30 jours.',
        'Les administrateurs et gestionnaires reçoivent un résumé par courriel (chaque jour, chaque semaine ou jamais, au choix dans Mon profil) : ce que les agents ont fait seuls et ce qui attend le conseil (seulement s’il y a quelque chose).',
        'Intégrité du journal : chaque événement et chaque action automatique est enchaîné au précédent par une empreinte; « Vérifier l’intégrité » recalcule tout et compare avec l’empreinte déposée chaque jour hors de la base de données.',
        'Les actions automatiques sont listées sur la même page. Certaines décisions restent toujours humaines : argent qui sort, amendes, cotisations spéciales, avis légaux, litiges, urgences.',
      ],
      en: [
        'Automation shows each automated process (replies to requests, payment reminders, bank reconciliation, insurance certificates, sending work to contractors, contractor invoices, bookings) and its mode.',
        'Confirmation (initial setting): the agent prepares, a board member checks and confirms. Automatic: the agent acts on its own, within specific guardrails, and logs it.',
        'Start in confirmation; once a process’s proposals have been right for a while, a director switches it to automatic (with a note for the history). You can go back to confirmation at any time.',
        'To know when to switch to automatic, look at each process’s accuracy: over 90 days, how many of the agent’s proposals the board confirmed as they were. "Ready for automatic mode" shows after at least 10 decisions, 95% of them confirmed.',
        'Sending work to contractors: first set the amount authorized without a quote (e.g. $500). In automatic mode, the contractor receives the work order with that amount; above it, they must send a quote. Emergencies always stay human.',
        'Supplier invoice approvals: set the maximum amount of an invoice approved on its own. Only invoices that match their work order and were read with confidence are approved; no payment is made.',
        'Booking approvals: a booking that meets the rules is approved on its own if the unit has nothing more than 30 days overdue.',
        'Directors and managers receive an email summary (daily, weekly or never, chosen in My profile): what the agents did on their own and what waits for the board (only when there is something).',
        'Log integrity: every event and every automatic action is chained to the previous one by a fingerprint; "Check integrity" recomputes everything and compares it with the fingerprint stored outside the database every day.',
        'Automatic actions are listed on the same page. Some decisions always stay human: money going out, fines, special assessments, legal notices, disputes, emergencies.',
      ],
    },
  },
  {
    id: 'onboarding',
    page: 'onboarding',
    requires: 'onboarding.manage',
    title: { fr: 'Mettre en service avec vos documents', en: 'Onboarding with your documents' },
    body: {
      fr: [
        'La mise en service est faite avec l’équipe Syndiqia avant l’activation de votre espace. Ensuite, le conseil peut y revenir (Mise en service) pour compléter ce qui avait été laissé pour plus tard : documents, finances, fournisseurs. Chaque étape dit ce qu’on cherche.',
        'Déposez ce que vous avez, plusieurs fichiers à la fois : PDF, Word (.docx), Excel (.xlsx), CSV ou texte. Syndiqia les lit en général en moins d’une minute.',
        'La liste des propriétaires en Excel ou CSV est lue exactement, ligne par ligne : Syndiqia reconnaît les colonnes (lot, nom, courriel, téléphone, langue, part). Une liste en PDF ou Word est lue aussi, jusqu’à 150 lots.',
        '« Ce que Syndiqia a trouvé » : ✓ ce qui est complet, ⚠ ce qui manque sans bloquer (des téléphones, des courriels, un procès-verbal), ✗ ce qu’il faut compléter avant d’appliquer (l’adresse principale, un lot sans propriétaire, l’exercice financier). Ajoutez d’autres fichiers à tout moment : l’étape est relue.',
        'Vérifiez l’aperçu, corrigez au besoin (par exemple l’adresse), puis « Appliquer ». Rien n’est enregistré avant : ni lots, ni personnes, ni écritures, ni documents.',
        'Appliquer les documents les classe (visibles par les membres ou par le conseil seulement) et, si vous le cochez, met à jour les règles d’assemblée trouvées dans les règlements.',
        'Appliquer les finances crée l’exercice, inscrit les soldes d’ouverture (comptes bancaires, fonds de réserve, montants dus par lot) et enregistre le budget en brouillon : son adoption reste une décision du conseil.',
        'Les données existantes ne sont jamais écrasées : un lot déjà attribué, des soldes d’ouverture déjà inscrits ou un budget adopté restent tels quels.',
      ],
      en: [
        'Onboarding is done with the Syndiqia team before your space is activated. Afterwards, the board can come back to it (Onboarding) to complete what was left for later: documents, finances, suppliers. Each step says what we are looking for.',
        'Drop what you have, several files at once: PDF, Word (.docx), Excel (.xlsx), CSV or text. Syndiqia usually reads them in under a minute.',
        'An owners list in Excel or CSV is read exactly, row by row: Syndiqia recognizes the columns (lot, name, email, phone, language, share). A list in PDF or Word is read too, up to 150 lots.',
        '"What Syndiqia found": ✓ what is complete, ⚠ what is missing without blocking (phones, emails, minutes), ✗ what must be completed before applying (the main address, a lot without an owner, the fiscal year). Add more files at any time: the step is read again.',
        'Check the preview, correct if needed (the address, for example), then "Apply". Nothing is saved before: no lots, people, entries or documents.',
        'Applying the documents files them (visible to members or to the board only) and, if you tick it, updates the meeting rules found in the by-laws.',
        'Applying the finances creates the fiscal year, records the opening balances (bank accounts, reserve fund, amounts owed by lot) and saves the budget as a draft: adopting it stays a board decision.',
        'Existing data is never overwritten: a lot that already has owners, opening balances already recorded or an adopted budget stay as they are.',
      ],
    },
  },
  {
    id: 'settings',
    page: 'settings',
    requires: 'registry.configure',
    title: { fr: 'Paramètres : immeubles, unités, règles, courriels', en: 'Settings: buildings, units, rules, emails' },
    body: {
      fr: [
        'Paramètres sert à la mise en service : immeubles et unités, et les règles des assemblées (quorum, délais d’avis) selon la déclaration ou les règlements.',
        'Réunions du conseil : le quorum des administrateurs, le délai d’avis, et si le président de la réunion a une voix prépondérante en cas d’égalité, selon vos règlements.',
        'Courriels envoyés : chaque courriel avec son statut de livraison (preuve de transmission).',
        'Le type de regroupement (syndicat de copropriétaires ou association de propriétaires) est fixé à l’ouverture du compte.',
      ],
      en: [
        'Settings is for setup: buildings and units, and meeting rules (quorum, notice periods) according to the declaration or by-laws.',
        'Board meetings: the directors’ quorum, the notice period, and whether the chair has a casting vote in a tie, according to your by-laws.',
        'Emails sent: each email with its delivery status (proof of delivery).',
        'The kind of community (syndicate of co-owners or owners’ association) is set when the account is opened.',
      ],
    },
  },
];
